import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

const SubscriptionBannerCtx = createContext(null);

// Tracks how many subscription banners are currently on screen so the layout
// can reserve room for them. Banners report in via useReportSubscriptionBanner
// — the layout never has to inspect the DOM to find out.
export const SubscriptionBannerProvider = ({ children }) => {
  const [visibleCount, setVisibleCount] = useState(0);

  const registerBanner = useCallback(() => {
    setVisibleCount((count) => count + 1);
    return () => setVisibleCount((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo(
    () => ({ isBannerVisible: visibleCount > 0, registerBanner }),
    [visibleCount, registerBanner]
  );

  return (
    <SubscriptionBannerCtx.Provider value={value}>
      {children}
    </SubscriptionBannerCtx.Provider>
  );
};

// Read by the main layout to offset page content below a visible banner.
export const useIsSubscriptionBannerVisible = () =>
  useContext(SubscriptionBannerCtx)?.isBannerVisible ?? false;

// Called by each banner component with whether it is currently rendering.
// A no-op outside the provider (e.g. blank/auth layouts), so banners can be
// rendered anywhere without crashing.
export const useReportSubscriptionBanner = (isShowing) => {
  const registerBanner = useContext(SubscriptionBannerCtx)?.registerBanner;

  useEffect(() => {
    if (!isShowing || !registerBanner) return undefined;
    return registerBanner();
  }, [isShowing, registerBanner]);
};
